import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mail, Phone, Calendar, MessageSquare, TrendingUp, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Activity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

export const ActivityFeed = () => {
  const { orgId } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    loadActivities();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('sales-activities')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `org_id=eq.${orgId}`,
        },
        () => loadActivities()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId]);

  const loadActivities = async () => {
    if (!orgId) return;

    try {
      const { data: comments } = await supabase
        .from('comments')
        .select(`
          *,
          lead:leads(contact_name)
        `)
        .eq('org_id', orgId)
        .eq('is_system_activity', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (comments) {
        const formattedActivities = comments.map(c => ({
          id: c.id,
          type: c.activity_type || 'update',
          description: c.content,
          timestamp: c.created_at,
          metadata: c.activity_metadata
        }));
        setActivities(formattedActivities);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'email_sent':
        return Mail;
      case 'call_completed':
        return Phone;
      case 'meeting_scheduled':
        return Calendar;
      case 'reply_received':
        return MessageSquare;
      case 'lead_converted':
        return TrendingUp;
      case 'ai_action':
        return Sparkles;
      default:
        return Mail;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'email_sent':
        return 'bg-blue-500/20 text-blue-600';
      case 'call_completed':
        return 'bg-green-500/20 text-green-600';
      case 'meeting_scheduled':
        return 'bg-purple-500/20 text-purple-600';
      case 'reply_received':
        return 'bg-orange-500/20 text-orange-600';
      case 'lead_converted':
        return 'bg-emerald-500/20 text-emerald-600';
      case 'ai_action':
        return 'bg-pink-500/20 text-pink-600';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Activity Feed</h3>
        </div>
        <p className="text-sm text-muted-foreground">Loading activities...</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary animate-pulse" />
        <h3 className="font-semibold">Live Activity Feed</h3>
        <Badge variant="secondary" className="ml-auto">Real-time</Badge>
      </div>
      
      <ScrollArea className="h-[300px] pr-4">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No recent activity
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = getActivityIcon(activity.type);
              return (
                <div 
                  key={activity.id}
                  className="flex gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-fade-in"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2">{activity.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </Card>
  );
};
